import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import { useSnack, SaveSnackbar } from './settingsHelper';

const CurrencySettings = () => {
  const { data } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [defaultCurrency, setDefaultCurrency] = useState('INR');
  // India-appropriate defaults, used until the store saves its own
  // custom.currencies list (absent on the seeded DB).
  const [currencies, setCurrencies] = useState([
    { id: 1, name: 'Indian Rupee', symbol: '₹', code: 'INR', exchangeRate: 83, status: true },
    { id: 2, name: 'US Dollar', symbol: '$', code: 'USD', exchangeRate: 1, status: true },
  ]);
  const [newCurrency, setNewCurrency] = useState({
    name: '',
    symbol: '',
    code: '',
    exchangeRate: '',
  });

  useEffect(() => {
    const sp = settings.systemPreferences || {};
    if (sp.currency !== undefined) setDefaultCurrency(sp.currency);
    const savedCurrencies = settings.custom?.currencies;
    if (Array.isArray(savedCurrencies) && savedCurrencies.length) setCurrencies(savedCurrencies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDefaultCurrencyChange = (event) => {
    setDefaultCurrency(event.target.value);
  };

  const handleNewCurrencyChange = (field, value) => {
    setNewCurrency((prev) => ({ ...prev, [field]: value }));
  };

  const isNewCurrencyValid = newCurrency.name.trim() !== '' || newCurrency.code.trim() !== '';

  const handleAddCurrency = () => {
    if (!isNewCurrencyValid) return;
    const id = currencies.reduce((max, c) => Math.max(max, Number(c.id) || 0), 0) + 1;
    setCurrencies([...currencies, { id, ...newCurrency, status: true }]);
    setNewCurrency({ name: '', symbol: '', code: '', exchangeRate: '' });
  };

  const handleCurrencyStatusChange = (index) => {
    const updatedCurrencies = [...currencies];
    updatedCurrencies[index].status = !updatedCurrencies[index].status;
    setCurrencies(updatedCurrencies);
  };

  const handleCurrencyDelete = (index) => {
    const updatedCurrencies = currencies.filter((_, i) => i !== index);
    setCurrencies(updatedCurrencies);
  };

  const handleSave = async () => {
    try {
      await updateSettings({
        systemPreferences: { currency: defaultCurrency },
        custom: { currencies },
      }).unwrap();
      showSuccess();
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Default Currency Setup</Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Currency"
          value={defaultCurrency}
          onChange={handleDefaultCurrencyChange}
        />
      </Grid>

      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6">Add Currency</Typography>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Currency Name"
            value={newCurrency.name}
            onChange={(e) => handleNewCurrencyChange('name', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Currency Symbol"
            value={newCurrency.symbol}
            onChange={(e) => handleNewCurrencyChange('symbol', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Currency Code"
            value={newCurrency.code}
            onChange={(e) => handleNewCurrencyChange('code', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Exchange Rate (1 USD = )"
            type="number"
            value={newCurrency.exchangeRate}
            onChange={(e) => handleNewCurrencyChange('exchangeRate', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddCurrency}
          disabled={!isNewCurrencyValid}
        >
          Add
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SL</TableCell>
              <TableCell>Currency Name</TableCell>
              <TableCell>Currency Symbol</TableCell>
              <TableCell>Currency Code</TableCell>
              <TableCell>Exchange Rate (1 USD = )</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currencies.map((currency, index) => (
              <TableRow key={currency.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{currency.name}</TableCell>
                <TableCell>{currency.symbol}</TableCell>
                <TableCell>{currency.code}</TableCell>
                <TableCell>{currency.exchangeRate}</TableCell>
                <TableCell>
                  <Switch checked={currency.status} onChange={() => handleCurrencyStatusChange(index)} />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleCurrencyDelete(index)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Grid>

      <SaveSnackbar snack={snack} onClose={closeSnack} />
    </Grid>
  );
};

export default CurrencySettings;
